import React from 'react';
import { brand } from '../theme';
import { Layout, Typography, Space, Tag, Anchor, Divider, Alert } from 'antd';
import {
  ApiOutlined,
  RobotOutlined,
  ApartmentOutlined,
  ShareAltOutlined,
  DeploymentUnitOutlined,
  ThunderboltOutlined,
  ShopOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';

const { Content, Sider } = Layout;
const { Title, Paragraph, Text } = Typography;

const Section: React.FC<{ id: string; title: string; icon?: React.ReactNode; children: React.ReactNode }> = ({
  id, title, icon, children,
}) => (
  <div id={id} style={{ marginBottom: 56 }}>
    <Space align="center" style={{ marginBottom: 16 }}>
      {icon && <span style={{ fontSize: 22, color: '#1677ff' }}>{icon}</span>}
      <Title level={2} style={{ margin: 0 }}>{title}</Title>
    </Space>
    {children}
  </div>
);

const Sub: React.FC<{ id: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
  <div id={id} style={{ marginBottom: 32 }}>
    <Title level={4} style={{ marginBottom: 12, color: '#262626' }}>{title}</Title>
    {children}
  </div>
);

const CodeBlock: React.FC<{ children: string }> = ({ children }) => (
  <pre style={{
    background: '#f6f8fa',
    border: '1px solid #e8e8e8',
    borderRadius: 8,
    padding: '12px 16px',
    fontSize: 13,
    lineHeight: 1.6,
    overflowX: 'auto',
    marginTop: 8,
    marginBottom: 8,
  }}>
    <code>{children}</code>
  </pre>
);

const Chip: React.FC<{ color?: string; children: React.ReactNode }> = ({ color = 'blue', children }) => (
  <Tag color={color} style={{ marginBottom: 4 }}>{children}</Tag>
);

const DocsPage: React.FC = () => {

  const anchorItems = [
    { key: 'intro', href: '#intro', title: 'Introducción' },
    {
      key: 'agents', href: '#agents', title: 'Agentes',
      children: [
        { key: 'agents-config', href: '#agents-config', title: 'Configuración' },
        { key: 'agents-providers', href: '#agents-providers', title: 'Proveedores de modelo' },
      ],
    },
    {
      key: 'flows', href: '#flows', title: 'Flujos',
      children: [
        { key: 'flows-nodes', href: '#flows-nodes', title: 'Tipos de nodo' },
        { key: 'flows-edges', href: '#flows-edges', title: 'Conexiones' },
      ],
    },
    {
      key: 'ontology', href: '#ontology', title: 'Ontología',
      children: [
        { key: 'ontology-entities', href: '#ontology-entities', title: 'Entidades' },
        { key: 'ontology-relationships', href: '#ontology-relationships', title: 'Relaciones' },
      ],
    },
    { key: 'kg', href: '#kg', title: 'Knowledge Graph' },
    { key: 'execution', href: '#execution', title: 'Ejecución' },
    { key: 'marketplace', href: '#marketplace', title: 'Marketplace' },
  ];

  return (
    <Layout style={{ height: 'calc(100vh - 50px)', background: 'transparent', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      <Layout style={{ background: 'transparent', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar navigation */}
        <Sider
          width={240}
          style={{
            background: '#fff',
            borderRight: '1px solid #f0f0f0',
            padding: '32px 0',
            height: '100%',
            overflowY: 'auto',
          }}
        >
          <div style={{ padding: '0 16px', marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
              Contenido
            </Text>
          </div>
          <Anchor
            items={anchorItems}
            offsetTop={80}
            style={{ padding: '0 16px' }}
          />
        </Sider>

        {/* Main content */}
        <Content style={{ padding: '40px 56px', maxWidth: 860, minWidth: 0, overflowY: 'auto', height: '100%' }}>
          {/* Intro */}
          <Section id="intro" title={brand.name} icon={<ApiOutlined />}>
            <Paragraph style={{ fontSize: 16, lineHeight: 1.8 }}>
              <strong>{brand.name}</strong> es una plataforma para crear, configurar y ejecutar agentes de inteligencia artificial de forma visual. Sin código, sin configuración compleja.
            </Paragraph>
            <Paragraph style={{ lineHeight: 1.8 }}>
              Permite diseñar el comportamiento de un agente definiendo su flujo de trabajo (qué nodos ejecuta y en qué orden), su ontología (qué entidades y relaciones conoce) y activarlo para recibir peticiones a través de un webhook.
            </Paragraph>
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 16 }}
              message="Componentes principales"
              description={
                <ul style={{ margin: '8px 0 0', paddingLeft: 20, lineHeight: 2 }}>
                  <li><strong>Builder</strong> — UI de configuración (este panel)</li>
                  <li><strong>Executor</strong> — Motor de ejecución Python (LangGraph)</li>
                  <li><strong>Base de datos</strong> — PostgreSQL compartida</li>
                  <li><strong>Neo4j</strong> — Knowledge Graph (opcional)</li>
                </ul>
              }
            />
          </Section>

          <Divider />

          {/* Agents */}
          <Section id="agents" title="Agentes" icon={<RobotOutlined />}>
            <Paragraph style={{ lineHeight: 1.8 }}>
              Un <strong>agente</strong> es la unidad principal de la plataforma. Define la identidad, el comportamiento y los recursos de la IA que quieres ejecutar.
            </Paragraph>

            <Sub id="agents-config" title="Configuración de un agente">
              <Paragraph style={{ lineHeight: 1.8 }}>
                Al crear o editar un agente configuras los siguientes campos:
              </Paragraph>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', width: '30%' }}>Campo</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>Descripción</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Nombre', 'Identificador legible del agente (ej: "Lead Enrichment")'],
                    ['Rol', 'Qué tipo de experto es el agente (ej: "analista de leads")'],
                    ['Tarea', 'Qué hace en una palabra (ej: "extracción de datos")'],
                    ['Descripción', 'Explicación detallada de su propósito'],
                    ['Output esperado', 'Formato o contenido que debe devolver'],
                    ['Proveedor', 'Motor de LLM: Vertex AI, OpenAI o Anthropic'],
                    ['Modelo', 'Nombre exacto del modelo (ej: gemini-2.5-flash)'],
                    ['Temperatura', 'Creatividad de las respuestas (0 = preciso, 1 = creativo)'],
                    ['Recursion limit', 'Número máximo de pasos que puede dar el grafo'],
                    ['Update KG', 'Si está activo, escribe resultados en el Knowledge Graph'],
                  ].map(([campo, desc]) => (
                    <tr key={campo} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px 12px' }}><Text code>{campo}</Text></td>
                      <td style={{ padding: '10px 12px', color: '#595959' }}>{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Paragraph style={{ marginTop: 16, lineHeight: 1.8 }}>
                El <strong>system prompt</strong> se genera automáticamente a partir del rol, tarea, descripción y output esperado. No es necesario escribirlo manualmente.
              </Paragraph>
            </Sub>

            <Sub id="agents-providers" title="Proveedores de modelo">
              <Space wrap style={{ marginBottom: 12 }}>
                <Chip color="green">Vertex AI</Chip>
                <Chip color="blue">OpenAI</Chip>
                <Chip color="purple">Anthropic</Chip>
              </Space>
              <Paragraph style={{ lineHeight: 1.8 }}>
                El executor selecciona automáticamente el cliente correcto según el proveedor. Si el proveedor no tiene API key configurada, hace <em>fallback</em> a <Text code>gemini-2.5-flash</Text> en Vertex AI.
              </Paragraph>
            </Sub>
          </Section>

          <Divider />

          {/* Flows */}
          <Section id="flows" title="Flujos" icon={<ApartmentOutlined />}>
            <Paragraph style={{ lineHeight: 1.8 }}>
              El <strong>flujo</strong> define el orden en que el agente ejecuta sus nodos. Se diseña visualmente en el canvas de la pestaña <em>Flujo</em> dentro de cada agente.
            </Paragraph>

            <Sub id="flows-nodes" title="Tipos de nodo">
              <Paragraph style={{ lineHeight: 1.8 }}>
                Cada nodo representa una tarea específica que el agente ejecuta. Se añaden desde la barra lateral del canvas.
              </Paragraph>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', width: '25%' }}>Tipo</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', width: '15%' }}>Estado</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>Descripción</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['extraction', '✅ Implementado', 'Extrae datos estructurados con un esquema Pydantic. Ideal para sacar campos concretos de un texto.'],
                    ['classification', '✅ Implementado', 'Clasifica el input en categorías predefinidas usando tipos Literal.'],
                    ['prompt', '✅ Implementado', 'Ejecuta un prompt genérico contra el LLM y devuelve la respuesta.'],
                    ['search', '🔄 En progreso', 'Búsqueda en fuentes externas (Tavily, web).'],
                    ['validation', '🔄 En progreso', 'Valida que los datos cumplen ciertas condiciones.'],
                    ['review', '🔄 En progreso', 'Revisión o aprobación humana de los resultados.'],
                    ['persistence', '🔄 En progreso', 'Guarda resultados en una base de datos externa.'],
                    ['bigquery_save', '✅ Implementado', 'Guarda todos los node_outputs en una tabla BigQuery automáticamente.'],
                    ['create_knowledge', '🔄 Auto', 'Se inyecta automáticamente al final si Update KG está activo. Escribe en Neo4j.'],
                  ].map(([tipo, estado, desc]) => (
                    <tr key={tipo} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px 12px' }}><Text code>{tipo}</Text></td>
                      <td style={{ padding: '10px 12px', fontSize: 12 }}>{estado}</td>
                      <td style={{ padding: '10px 12px', color: '#595959' }}>{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Paragraph style={{ marginTop: 16, lineHeight: 1.8 }}>
                Cada nodo recibe el <strong>estado acumulado</strong> del grafo (<Text code>node_outputs</Text>) y añade su propio resultado. Los nodos downstream pueden leer los resultados de los nodos anteriores.
              </Paragraph>
            </Sub>

            <Sub id="flows-edges" title="Conexiones entre nodos">
              <Paragraph style={{ lineHeight: 1.8 }}>
                Las <strong>conexiones</strong> (edges) definen el orden de ejecución. Para conectar dos nodos:
              </Paragraph>
              <ol style={{ lineHeight: 2, paddingLeft: 20 }}>
                <li>Haz clic en el punto de salida del nodo origen (lado derecho)</li>
                <li>Arrastra hasta el punto de entrada del nodo destino (lado izquierdo)</li>
                <li>La conexión se guarda automáticamente al guardar el flujo</li>
              </ol>
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message="El flujo debe tener exactamente un nodo START y todos los caminos deben llegar a END"
              />
            </Sub>
          </Section>

          <Divider />

          {/* Ontology */}
          <Section id="ontology" title="Ontología" icon={<ShareAltOutlined />}>
            <Paragraph style={{ lineHeight: 1.8 }}>
              La <strong>ontología</strong> es el esquema conceptual que define qué tipo de entidades y relaciones conoce el agente. Es la base para construir el Knowledge Graph.
            </Paragraph>
            <Paragraph style={{ lineHeight: 1.8 }}>
              Se gestiona en la pestaña <em>Ontología</em> dentro de cada agente. Los cambios se guardan versionados en PostgreSQL.
            </Paragraph>

            <Sub id="ontology-entities" title="Entidades">
              <Paragraph style={{ lineHeight: 1.8 }}>
                Una <strong>entidad</strong> representa un tipo de objeto del mundo real (ej: <Text code>Person</Text>, <Text code>Company</Text>, <Text code>Product</Text>).
              </Paragraph>
              <Paragraph style={{ lineHeight: 1.8 }}>
                Cada entidad tiene un nombre y una lista de <strong>propiedades</strong>:
              </Paragraph>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>Tipo de propiedad</th>
                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>Uso</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['string', 'Texto libre (nombre, email, descripción)'],
                    ['number', 'Valores numéricos (edad, precio, score)'],
                    ['boolean', 'Verdadero / Falso (activo, verificado)'],
                    ['date', 'Fechas (creado_en, fecha_contrato)'],
                  ].map(([tipo, uso]) => (
                    <tr key={tipo} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '10px 12px' }}><Text code>{tipo}</Text></td>
                      <td style={{ padding: '10px 12px', color: '#595959' }}>{uso}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Sub>

            <Sub id="ontology-relationships" title="Relaciones">
              <Paragraph style={{ lineHeight: 1.8 }}>
                Una <strong>relación</strong> conecta dos entidades con un verbo en mayúsculas (convención CYPHER). Ejemplos:
              </Paragraph>
              <Space wrap>
                <Chip color="geekblue">Person → WORKS_AT → Company</Chip>
                <Chip color="geekblue">Company → LOCATED_IN → City</Chip>
                <Chip color="geekblue">Person → KNOWS → Person</Chip>
              </Space>
              <Paragraph style={{ marginTop: 12, lineHeight: 1.8 }}>
                El grafo de preview de la UI muestra las entidades como nodos circulares y las relaciones como aristas etiquetadas. Se actualiza en tiempo real al añadir o eliminar elementos.
              </Paragraph>
            </Sub>
          </Section>

          <Divider />

          {/* Knowledge Graph */}
          <Section id="kg" title="Knowledge Graph" icon={<DeploymentUnitOutlined />}>
            <Paragraph style={{ lineHeight: 1.8 }}>
              Cuando el flag <strong>Update Knowledge Graph</strong> está activado en un agente, al finalizar cada ejecución se escribe automáticamente en Neo4j.
            </Paragraph>
            <Paragraph style={{ lineHeight: 1.8 }}>
              El flujo exacto es:
            </Paragraph>
            <ol style={{ lineHeight: 2.2, paddingLeft: 20 }}>
              <li>Al <strong>activar el agente</strong>, el executor carga la última versión de la ontología desde PostgreSQL.</li>
              <li>Al <strong>compilar el grafo</strong>, se inyecta automáticamente un nodo <Text code>create_knowledge</Text> al final del flujo. Todos los edges que iban a <Text code>__end__</Text> pasan primero por este nodo.</li>
              <li>Durante la <strong>ejecución</strong>, <Text code>create_knowledge</Text> recibe todos los <Text code>node_outputs</Text> acumulados y la ontología, genera las queries CYPHER con un LLM y las escribe en Neo4j.</li>
            </ol>
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 16 }}
              message="Requisito: Neo4j local"
              description={
                <span>
                  Para desarrollo local, arranca el contenedor Docker:
                  <CodeBlock>cd builder && docker-compose up -d neo4j</CodeBlock>
                  Neo4j Browser: <Text code>http://localhost:7474</Text>
                  {' '}— Usuario: <Text code>neo4j</Text> / Contraseña: <Text code>cubic_dev_2024</Text>
                </span>
              }
            />
          </Section>

          <Divider />

          {/* Execution */}
          <Section id="execution" title="Ejecución" icon={<ThunderboltOutlined />}>
            <Paragraph style={{ lineHeight: 1.8 }}>
              Para ejecutar un agente, primero hay que <strong>activarlo</strong>. Esto construye el grafo LangGraph en memoria a partir de la configuración guardada.
            </Paragraph>

            <Sub id="execution-activate" title="Activación">
              <Paragraph style={{ lineHeight: 1.8 }}>
                Pulsa el botón <strong>Activar</strong> en la página del agente. El executor responde con una webhook URL:
              </Paragraph>
              <CodeBlock>{`POST /agent/builder/{agent_id}/activate
→ { webhook_url: "/agent/builder/webhook/{agent_id}", nodes_count: 3 }`}</CodeBlock>
            </Sub>

            <Sub id="execution-webhook" title="Invocación via webhook">
              <Paragraph style={{ lineHeight: 1.8 }}>
                Una vez activado, envía peticiones al webhook:
              </Paragraph>
              <CodeBlock>{`POST /agent/builder/webhook/{agent_id}
Content-Type: application/json

{ "input": "Texto o datos que procesa el agente" }

→ { "output": "Resultado del agente" }`}</CodeBlock>
            </Sub>

            <Sub id="execution-history" title="Historial">
              <Paragraph style={{ lineHeight: 1.8 }}>
                Todas las ejecuciones quedan registradas en la pestaña <em>Ejecuciones</em> del agente. Puedes ver el input, el output y el timestamp de cada llamada.
              </Paragraph>
              <Paragraph style={{ lineHeight: 1.8 }}>
                Las trazas detalladas (latencia, tokens, pasos del grafo) están disponibles en <strong>LangSmith</strong>, proyecto <Text code>MVP_Agent</Text>.
              </Paragraph>
            </Sub>
          </Section>

          <Divider />

          {/* Marketplace */}
          <Section id="marketplace" title="Marketplace" icon={<ShopOutlined />}>
            <Paragraph style={{ lineHeight: 1.8 }}>
              El <strong>Marketplace</strong> contiene agentes predefinidos listos para usar. Actualmente incluye:
            </Paragraph>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Alert
                icon={<DatabaseOutlined />}
                showIcon
                type="success"
                message="Meeting Doubts Agent"
                description="Analiza reuniones y extrae las dudas, decisiones y próximos pasos. Recibe una transcripción y devuelve un resumen estructurado."
              />
            </Space>
            <Paragraph style={{ marginTop: 16, lineHeight: 1.8 }}>
              Los agentes de marketplace se identifican con el flag <Text code>isMarketplace: true</Text> y tienen una <Text code>marketplaceKey</Text> única. No son editables desde el Builder general.
            </Paragraph>
          </Section>

          <div style={{ height: 80 }} />
        </Content>
      </Layout>
    </Layout>
  );
};

export default DocsPage;
